import graphql__db49f8b6a304fdf6a6d0 from "./__generated__/FormPage_contribution.graphql.js";import { useRef, useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { defineMessages } from "react-intl";
import invariant from "invariant";

import withFormStore from "../Form/withFormStore";
import Modal from "../Modals/Modal";
import AnonymousContributionFormBanner from "../Contributions/AnonymousContribution/AnonymousContributionFormBanner";
import Answers from "../Answer/Answers";
import ContributionFormModalTitle from "../Contributions/ContributionFormModalTitle";
import ButtonLink from "../Buttons/ButtonLink";
import { hasHtmlContent } from "../Utils/htmlStringUtils";
import FormSubmitButton from "./FormSubmitButton";
import { useFormatMessage } from "../Intl/Context";
import RichText from "../Utils/RichText";
import { useAlert } from "../Alerts/withAlert";
import ContributionLocationMap from "./ContributionLocationMap";
import { useCurrentContributionsConnections } from "../Utils/contributionsConnections";

import FormPageCorrectionLegend from "../Input/FormPageCorrectionLegend";
import { useAuthenticationManager } from "../GeneralServices/AuthenticationService";
import { isAuthorized } from "../Utils/authorizations";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import { finalizeCommentable } from "../Utils/finalizeCommentable";
import { FormPageContext } from "./FormPageContext";

import { contributionFormStepBack } from "../../mutations/ContributionFormStepBackMutation";
import { validateContribution } from "../../mutations/ValidateContributionMutation";
import { toggleContributionAnonymousMode } from "../../mutations/ToggleContributionAnonymousModeMutation";
import { hideAlert } from "../../reducers/alerts";

const FormPage = ({ edit = false, ...props }) => {
  const contribution = useFragment(FormPage_contribution, props.contribution);
  const { validateForm, formState, redirectPath, currentPageId, pushErrors } = props;
  const {
    contribuable,
    currentPage,
    authorizations,
    hasNextPage,
    fromAnonymous,
    hasPreviousPage
  } = contribution;
  const { title } = contribution.currentPage || {};

  const navigate = useNavigate();

  const alert = useSelector(({ alert }) => alert);

  const t = useFormatMessage();
  const breadcrumbsTitle = t(intlMessages.title);
  const submitLabel = hasNextPage
    ? t(intlMessages.submitAndGoToNextPage)
    : t(intlMessages.submit);
  const requiredFieldsHint = t(intlMessages.requiredFieldsHint);
  const cancel = t(intlMessages.cancel);
  const signInMessage = t(intlMessages.signInMessage);

  const breadcrumbs = [{ breadcrumbsTitle }];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStepBackSubmitting, setIsStepBackSubmitting] = useState(false);

  const allowFormStepBack = isAuthorized(authorizations.formStepBack);

  const handleMobileStepBack = useCallback(() => {
    setIsStepBackSubmitting(true);
    contributionFormStepBack(contribution, () => setIsStepBackSubmitting(false));
  }, [contribution]);

  const { showAlert } = useAlert();

  const dispatch = useDispatch();

  const handleAnonymousModeChange = useCallback(
    anonymous => {
      toggleContributionAnonymousMode(contribution, anonymous);
    },
    [contribution]
  );

  const formActionsContainerRef = useRef();

  const removeErrorAlert = useCallback(() => {
    if (!alert || alert.type !== ALERT_TYPE_ERROR) return;

    dispatch(hideAlert());
  }, [alert, dispatch]);

  // Fetch the current active contribution and markers connection ids to provide them to
  // the validateContribution mutation execution function below. This is mandatory to
  // ensure that the currently active connections are updated when a new contribution is
  // published through the mutation.
  const contributionsConnections = useCurrentContributionsConnections(contribuable);

  const { authenticate } = useAuthenticationManager();

  const hasRequiredAnswers = useMemo(
    () => contribution.answers.find(({ formField }) => formField.required),
    [contribution.answers]
  );

  const authenticationAdditionalProps = useMemo(
    () => ({ contributionId: contribution.id }),
    [contribution.id]
  );

  // Declare `submitForm` here to allow referencing it in the `handleMutationResponse`
  // callback.
  let submitForm;

  const handleMutationResponse = useCallback(
    ({ validateContribution }) => {
      const {
        success,
        notice,
        needsAuthentication,
        errors,
        contribution: updatedContribution
      } = validateContribution;

      if (errors && errors.length > 0) {
        pushErrors(errors);
      }

      if (notice) {
        const type = success ? ALERT_TYPE_SUCCESS : ALERT_TYPE_ERROR;
        showAlert({ message: notice, type });
      }

      if (!success) {
        setIsSubmitting(false);
      }

      // If the contribution could not be saved because of the viewer needs to be
      // authenticated, which is the case when a branch condition makes the user jump to
      // the end of the form, we authenticate the user with the authentication mode provided
      // by the `needsAuthentication` authorization code, and submit back the form once it's
      // done.
      if (needsAuthentication) {
        authenticate(needsAuthentication, submitForm, authenticationAdditionalProps);
      }

      if (
        success &&
        updatedContribution &&
        !updatedContribution.isDraft &&
        !updatedContribution.hasNextPage &&
        // we ensure that the finalizeContribution function is called in the admin context
        // by checking if the finalizeUrl is present in the contribution object. Since the
        // finalizeUrl is only present in the admin context.
        contribution.finalizeUrl
      ) {
        finalizeCommentable(contribution.finalizeUrl, (message, error) => {
          if (error) {
            showAlert({ message, type: ALERT_TYPE_ERROR });
            setIsSubmitting(false);
          }
        });
      }
    },
    [
      contribution.finalizeUrl,
      pushErrors,
      showAlert,
      authenticate,
      submitForm,
      authenticationAdditionalProps
    ]
  );

  submitForm = useCallback(() => {
    // The submit button is disabled while a submission is pending, but the form can still
    // be submitted with the enter key, which would validate the same page twice and make
    // the second submission land on a contribution that has no current page anymore.
    if (isSubmitting) return;

    if (!validateForm()) return;
    removeErrorAlert();

    validateContribution(
      contribution,
      currentPageId,
      formState,
      edit,
      handleMutationResponse,
      contributionsConnections
    );

    setIsSubmitting(true);
  }, [
    contribution,
    contributionsConnections,
    currentPageId,
    edit,
    formState,
    handleMutationResponse,
    isSubmitting,
    removeErrorAlert,
    validateForm
  ]);

  const handleFormSubmitted = useCallback(
    e => {
      e.preventDefault();
      submitForm();
    },
    [submitForm]
  );

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      // Here we use Turbo to really close the modal on the admin side so we check
      // if we are using Turbo, if so we use Turbo to close the modal otherwise we
      // use the navigate function to go back to the previous page.
      if (window.Turbo) {
        window.Turbo.visit(redirectPath);
      } else {
        navigate(redirectPath);
      }
    }
  }, [navigate, redirectPath, isSubmitting]);

  // When the ValidateContributionMutation response is processed and the form is complete,
  // Relay seems to first update the FormPage component before the ContributionForm one.
  // The contribution is therefore rendered with an empty `currentPage`, which forces us
  // to stop the rendering here to avoid further errors. This allows the parent
  // ContributionForm component to handle the completed form case.
  //
  // We also handle here the case where the Relay store updates the currentPage.id value
  // with a value that's different than the one passed in the props as `currentPageId`
  // from the parent component, which means that we're in a transition between rendering
  // one form page and another, and we want to avoid to render the page with the wrong
  // fields, which messes up the form redux store.
  if (!currentPage || currentPageId !== currentPage.id) {
    return null;
  }

  const { description, isCorrection, correctionTextOnlyOnWrongAnswers } = currentPage;
  const submitAuthorization = edit
    ? authorizations.editContribution
    : authorizations.validateContribution;

  const mobileBackButton =
    hasPreviousPage && allowFormStepBack && !isStepBackSubmitting
      ? handleMobileStepBack
      : undefined;

  return (
    <Modal
      breadcrumbs={breadcrumbs}
      title={title}
      displayTitle={false}
      onRequestClose={handleClose}
      className="modal-with-actions"
      onMobileBack={mobileBackButton}
    >
      <FormPageContext.Provider value={{ formActionsContainerRef }}>
        <ContributionFormModalTitle title={title} contribution={contribution} />

        <AnonymousContributionFormBanner
          anonymousMode={contribution}
          anonymous={fromAnonymous}
          onChange={handleAnonymousModeChange}
        />

        {hasHtmlContent(description) && (
          <RichText
            className="box margin-bottom-xs padding-x-lg padding-y-md"
            content={description}
          />
        )}

        <ContributionLocationMap
          contribution={contribution}
          className="box box-image margin-bottom-xs"
        />

        {isCorrection && (
          <FormPageCorrectionLegend className="box margin-bottom-xs padding-x-lg padding-y-md" />
        )}

        <form
          onSubmit={handleFormSubmitted}
          className="form-page form-with-modal-actions"
        >
          {!isCorrection && hasRequiredAnswers && (
            <p
              className="form-hint-required-fields box padding-x-lg padding-top-md"
              dangerouslySetInnerHTML={{ __html: requiredFieldsHint }}
            />
          )}

          <Answers
            answers={contribution.answers}
            resourceId={contribution.id}
            className="box padding-x-lg padding-y-md margin-bottom-xs"
            isCorrection={isCorrection}
            correctionTextOnlyOnWrongAnswers={correctionTextOnlyOnWrongAnswers}
          />

          <div
            className="modal-actions modal-buttons-container"
            ref={formActionsContainerRef}
          >
            <FormSubmitButton
              onSignIn={submitForm}
              submitLabel={submitLabel}
              signInMessage={signInMessage}
              beforeAuthenticate={validateForm}
              authorization={submitAuthorization}
              isSubmitting={isSubmitting}
              additionalProps={authenticationAdditionalProps}
            />

            <ButtonLink
              href={redirectPath}
              className="btn btn-primary-inverse btn-block-md box-shadow-lg modal-action"
              disabled={isSubmitting}
              data-loading={isSubmitting}
              label={cancel}
              iconClassName="icon-suppr"
              iconPosition="after"
              data-contribution-form-cancel-button
            />
          </div>
        </form>
      </FormPageContext.Provider>
    </Modal>
  );
};

const FormPageWithFormStore = withFormStore(FormPage, {
  formKey: ({ formKey }) => {
    // Ensure that the component receives a formKey prop
    invariant(formKey, "The <FormPage> component must be passed the `formKey` prop.");

    return formKey;
  },
  clearFormOnUnmount: false
});

const FormPage_contribution = graphql__db49f8b6a304fdf6a6d0;

export default FormPageWithFormStore;

export const intlMessages = defineMessages({
  title: {
    id: "contribution.form.title",
    defaultMessage: "Contribuer"
  },
  requiredFieldsHint: {
    id: "contribution.form.requiredFieldsHint",
    defaultMessage: "Les champs marqués d'un astérisque (*) sont obligatoires."
  },
  signInMessage: {
    id: "contribution.form.signInMessage",
    defaultMessage: `
      Merci de vous identifier pour pouvoir valider le dépôt de votre contribution.
    `
  },
  submitAndGoToNextPage: {
    id: "contribution.form.submitAndGoToNextPage",
    defaultMessage: "Suivant"
  },
  submit: {
    id: "contribution.form.submit",
    defaultMessage: "Valider"
  },
  cancel: {
    id: "contribution.form.cancel",
    defaultMessage: "Annuler"
  }
});
